import UnavailableResponse from '@/models/unavailableResponse';
import DeliveryOption from '@/models/deliveryOption';
import ProductAvailability from '@/models/productAvailability';
import ProductShipping from '@/models/productShipping';
import dateHelpers from '@/helpers/date-helpers';
import { DAYS_OF_WEEK } from '@/constants/date-time';
import { SHIPPING_TYPES } from '@/constants/availability-response';
import { UNAVAILABLE_CODES } from '@/constants/no-results';

const UNSET_CACHE_VALUE = undefined;

export default class AvailabilityResponse {
  constructor(apiResponse, zipCode) {
    this._initializePrivateCacheProperties();

    this.apiResponse = apiResponse;
    this.isAvailable = apiResponse.isAvailable;
    this.unavailableSkus = apiResponse.unavailableSkus;

    const productDetails = apiResponse.products;
    const skus = Object.keys(productDetails);

    this.isSingleProduct = skus.length === 1;
    this.productAvailabilities = [];
    this.productShippings = [];
    skus.forEach(sku => {
      this.productAvailabilities.push(new ProductAvailability(sku, productDetails[sku].isAvailable, productDetails[sku].unavailableDetails, zipCode));
      this.productShippings.push(new ProductShipping(sku, productDetails[sku].shippingType, productDetails[sku].shippingDetails));
    });

    this.unavailableCode = null;
    this.unavailableDefaultMessage = null;

    if (!this.isAvailable) {
      this.unavailableCode = this.isSingleProduct ? this.productAvailabilities[0].unavailableCode : UNAVAILABLE_CODES.NO_INTERSECTING_OPTIONS;
      this.unavailableData = this.isSingleProduct ? this.productAvailabilities[0].unavailableData : null;
      const unavailableResponse = new UnavailableResponse(this.unavailableCode, this.unavailableData, zipCode);
      this.unavailableDefaultMessage = unavailableResponse.message();
    }

    const floristCutoffShipping = this.earliestFloristCutoffProductShipping();
    this.destinationFloristCutoff = !!floristCutoffShipping ? floristCutoffShipping.destinationFloristCutoff : null;
    this.destinationFloristCutoffSource = !!floristCutoffShipping ? floristCutoffShipping.destinationFloristCutoffSource : null;
    this.destinationUtcOffsetMinutes = !!floristCutoffShipping ? floristCutoffShipping.destinationUtcOffsetMinutes : null;

    this.destinationToday = apiResponse.recipientToday;
    this.holidayUnavailableHtml = apiResponse.holidayUnavailableHtml;
    this.weatherStatementHtml = apiResponse.weatherStatementHtml;
    this.deliveryOptions = apiResponse.deliveryOptions.map(o => new DeliveryOption(o, this.destinationToday));
  }

  /**
   * Returns response-level shipping type calculated according to the product shipping types
   *
   * @returns String
   */
  shippingType() {
    if (this._shippingTypeCache !== UNSET_CACHE_VALUE) {
      return this._shippingTypeCache;
    }

    const allTypes = this.productShippings.map(s => s.type);
    const uniqueTypes = [...new Set(allTypes)];

    const type = uniqueTypes.length === 1
      ? uniqueTypes[0]
      : SHIPPING_TYPES.MIXED;

    return this._shippingTypeCache = type;
  }

  /**
   * Is this for florist product shipping types only
   *
   * @returns Boolean
   */
  isFloristShipping() {
    if (this._isFloristShippingCache !== UNSET_CACHE_VALUE) {
      return this._isFloristShippingCache;
    }

    return this._isFloristShippingCache = this.shippingType() === SHIPPING_TYPES.FLORIST;
  }

  /**
   * Is this for dropship product shipping types only
   *
   * @returns Boolean
   */
  isDropshipShipping() {
    if (this._isDropshipShippingCache !== UNSET_CACHE_VALUE) {
      return this._isDropshipShippingCache;
    }

    return this._isDropshipShippingCache = this.shippingType() === SHIPPING_TYPES.DROPSHIP;
  }

  /**
   * Is this for a mix of florist and dropship product shipping types
   *
   * @returns Boolean
   */
  isMixedShipping() {
    if (this._isMixedShippingCache !== UNSET_CACHE_VALUE) {
      return this._isMixedShippingCache;
    }

    return this._isMixedShippingCache = this.shippingType() === SHIPPING_TYPES.MIXED;
  }

  /**
   * Is same day delivery offered for the requested products
   *
   * Note that we limit this to florist products and short-circuit if not since, despite how
   * we want it to behave, the business and API do not support dropship products being delivered
   * same day. If/When that moment comes, we will need answers from stakeholders about messaging
   * and behaviors, particularly with the Same Day Message and GA Events
   *
   * @returns Boolean
   */
  offersSameDayDelivery() {
    if (this._offersSameDayDeliveryCache !== UNSET_CACHE_VALUE) {
      return this._offersSameDayDeliveryCache;
    }

    return this._offersSameDayDeliveryCache = this.isFloristShipping() && this.productShippings.every(ps => !!ps.destinationFloristCutoff);
  }

  /**
   * Returns the ProductShipping with the earliest destinationFloristCutoffs if all products are florist
   * otherwise null
   *
   * @returns ProductShipping|null
   */
  earliestFloristCutoffProductShipping() {
    if (this._earliestFloristCutoffProductShippingCache !== UNSET_CACHE_VALUE) {
      return this._earliestFloristCutoffProductShippingCache;
    }

    let productShipping = null;
    if (this.offersSameDayDelivery()) {
      productShipping = this.productShippings.reduce(
        (previousShipping, currentShipping) => {
          return currentShipping.destinationFloristCutoff < previousShipping.destinationFloristCutoff ? currentShipping : previousShipping;
        }
      )
    }

    return this._earliestFloristCutoffProductShippingCache = productShipping;
  }

  /**
   * The Delivery Option representing the first _possible_ delivery date option
   * according to the calendar, if it is returned in the delivery options
   *
   * @returns DeliveryOption|null
   */
  firstPossibleDeliveryDate() {
    if (this._firstPossibleDeliveryDateCache !== UNSET_CACHE_VALUE) {
      return this._firstPossibleDeliveryDateCache;
    }

    return this._firstPossibleDeliveryDateCache = this.deliveryOptions.find(o => o.isFirstPossibleDeliveryDate());
  }

  /**
   * The Delivery Option representing today, if it is returned in the delivery options
   *
   * @returns DeliveryOption|null
   */
  todaysDeliveryDate() {
    if (this._todaysDeliveryDateCache !== UNSET_CACHE_VALUE) {
      return this._todaysDeliveryDateCache;
    }

    return this._todaysDeliveryDateCache = this.deliveryOptions.find(o => o.isToday());
  }

  /**
   * The first flex-date Delivery Option, if it is returned in the delivery options
   *
   * @returns DeliveryOption|null
   */
  firstFlexOption() {
    if (this._firstFlexOptionCache !== UNSET_CACHE_VALUE) {
      return this._firstFlexOptionCache;
    }

    return this._firstFlexOptionCache = this.deliveryOptions.find((o) => o.isFlex());
  }

  /**
   * All unavailable options in deliveryOptions regardless of type
   *
   * @returns Array|null
   */
  unavailableOptions() {
    if (this._unavailableOptionsCache !== UNSET_CACHE_VALUE) {
      return this._unavailableOptionsCache;
    }

    return this._unavailableOptionsCache = this.deliveryOptions.filter(o => !o.isAvailable);
  }

  /**
   * All unavailable dates in deliveryOptions
   *
   * @returns Array|null
   */
  unavailableDates() {
    if (this._unavailableDatesCache !== UNSET_CACHE_VALUE) {
      return this._unavailableDatesCache;
    }

    return this._unavailableDatesCache = this.deliveryOptions.filter(o => !o.isAvailable && o.isDate);
  }

  /**
   * Is there at least one unavailable Sunday in the returned options?
   *
   * @returns Boolean
   */
  hasUnavailableSundays() {
    if (this._hasUnavailableSundaysCache !== UNSET_CACHE_VALUE) {
      return this._hasUnavailableSundaysCache;
    }

    return this._hasUnavailableSundaysCache = this.unavailableDates().some(o => o.dayOfWeek === DAYS_OF_WEEK.SUNDAY);
  }

  /**
   * Is there at least one unavailable Saturday in the returned options?
   *
   * @returns Boolean
   */
  hasUnavailableSaturdays() {
    if (this._hasUnavailableSaturdaysCache !== UNSET_CACHE_VALUE) {
      return this._hasUnavailableSaturdaysCache;
    }

    return this._hasUnavailableSaturdaysCache = this.unavailableDates().some(o => o.dayOfWeek === DAYS_OF_WEEK.SATURDAY);
  }

  /**
   * Does this contain the first _possible_ delivery date and does that date
   * have a relative delivery-day-index fee that is sourced from the zip code?
   *
   * @returns Boolean
   */
  hasSameDayFeeFromZipCode() {
    if (this._hasSameDayFeeFromZipCodeCache !== UNSET_CACHE_VALUE) {
      return this._hasSameDayFeeFromZipCodeCache;
    }

    return this._hasSameDayFeeFromZipCodeCache = !!this.firstPossibleDeliveryDate() && this.firstPossibleDeliveryDate().hasRelativeFeeFromZipCode();
  }

  /**
   * The cutoff hour value, calculated from the returned sameDayCutoffUtc & recipientUtcOffset
   *
   * @returns Number
   */
  destinationFloristCutoffHour() {
    if (this._destinationFloristCutoffHourCache !== UNSET_CACHE_VALUE) {
      return this._destinationFloristCutoffHourCache;
    }

    const clientOffsetCutoffDate = new Date(this.destinationFloristCutoff);
    const destinationCutoffTime = dateHelpers.getDestinationTime(this.destinationUtcOffsetMinutes, clientOffsetCutoffDate);
    const destinationCutoffDate = new Date(destinationCutoffTime);
    return this._destinationFloristCutoffHourCache = destinationCutoffDate.getHours();
  }

  /**
   * Called only during initialization to explicitly set all
   * "private" properties used for caching method values
   * Technically, we could just not initialize these and they would
   * implicitly be set to undefined, but this feels like a slightly
   * safer guard against quirky browser/type behavior
   *
   * @returns void
   */
  _initializePrivateCacheProperties() {
    this._ShippingTypeCache
      = this._isFloristShippingCache
      = this._isDropshipShippingCache
      = this._isMixedShippingCache
      = this._offersSameDayDeliveryCache
      = this._earliestFloristCutoffProductShippingCache
      = this._firstPossibleDeliveryDateCache
      = this._todaysDeliveryDateCache
      = this._firstFlexOptionCache
      = this._unavailableOptionsCache
      = this._unavailableDatesCache
      = this._hasUnavailableSundaysCache
      = this._hasUnavailableSaturdaysCache
      = this._hasSameDayFeeFromZipCodeCache
      = this._destinationFloristCutoffHourCache
      = UNSET_CACHE_VALUE;
  }
}
